import React, { SyntheticEvent, useCallback, useLayoutEffect, useMemo } from 'react';
import {
  useNavigate,
  useParams,
  useActionData,
  useAsyncValue,
  useRevalidator,
  useSubmit,
} from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

// Redux
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';

// Components Styled
import { Container } from './styles';

//Components
import ContentCardResume from '../../../../../components/display/ContentCardResume';

// Types
import { type NewCommunication } from '../../../../../types/communication';
import { createFormDataFromValues } from '../../../../../utils/forms';
import { OrderType } from '../../../../../types/orders';
import { Role } from '../../../../../types/auth';

// Hooks
import { useAuth } from '../../../../../hooks/useAuth';

// Utils
import { readOwnCommunicationNavigationContext } from '../../../../../utils/communicationEditDraft';

// Constants
import { getClassificationName } from '../../../../../utils/orders';

export default function EditCommunicationSummaryPage(): JSX.Element {
  const { t } = useTranslation();
  const loaderData = useAsyncValue() as NewCommunication;
  const actionData = useActionData() as NewCommunication;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const submit = useSubmit();
  const dispatch = useDispatch();

  // Get current user role - Tablet cannot change communication state
  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;

  // Read navigation context from session storage for context-aware redirect after save
  // Solo el contexto de ESTA comunicación (`entityId` = `:id`); el de otra
  // visita se ignora (no se borra) y se vuelve al detalle (S2).
  const { id: urlOrderId } = useParams();
  const navigationContext = useMemo(
    () => readOwnCommunicationNavigationContext(urlOrderId),
    [urlOrderId]
  );

  const communicationData = actionData ?? loaderData;

  useLayoutEffect(() => {
    if (!communicationData) navigate('..');
    if (communicationData?.roid && communicationData?.id) {
      dispatch(
        roidOTSelected(
          OrderType.Communication,
          Array.isArray(communicationData.roid)
            ? communicationData.roid.join('.')
            : communicationData.roid,
          communicationData.id as string
        )
      );
    }
  }, [communicationData]);

  // Volver al paso anterior del wizard. Este componente tuvo dos vidas hasta
  // 2026-08-04: paso "Resum" de la edición Y detalle de consulta de Informes
  // (`/reporting/.../summary/:id`), donde este mismo botón era la ENTRADA a la
  // edición. Esa segunda vida ya no existe —Informes manda al detalle del
  // módulo—, así que aquí solo queda el paso atrás del wizard.
  const handleEditCommunication = useCallback(() => {
    navigate('..');
  }, [navigate]);

  const handleSaveData = async (event: SyntheticEvent) => {
    event.preventDefault();
    const formData = createFormDataFromValues({
      ...actionData.dataDiff,
      intent: 'save-data',
      parentOrderId: actionData.parentOrderId,
      // Context-aware redirect: pass navigation context to action
      navigationOrigin: navigationContext?.origin || 'communications-table',
      workOrderId: navigationContext?.parentOrderId || '',
      // `returnTo` lo escribe quien abrió la edición (las pestañas de campaña
      // mandan el suyo). El id de la comunicación NO se manda desde aquí: lo
      // pone la action desde el parámetro de la ruta, que es la autoridad —
      // ver el comentario en `../action.ts`.
      returnTo: navigationContext?.returnTo || '',
    });
    submit(formData, {
      method: 'POST',
      action: '.',
    });
  };

  const refetchData = () => {
    revalidator.revalidate();
  };
  return (
    <Container>
      {communicationData && (
        <ContentCardResume
          title={t('communications:summary')}
          roid={communicationData.roid}
          operator={communicationData.operator}
          data={format(
            new Date(communicationData.registerInitDate as string),
            'dd / MM / yyyy'
          )}
          hour={format(
            new Date(communicationData.registerInitDate as string),
            'HH:mm'
          )}
          groupInter={communicationData.groupInter}
          media={communicationData.media}
          sendStatus={communicationData.sendStatus}
          capitol={communicationData.capitol}
          subCapitol={communicationData.subCapitol}
          classification={getClassificationName(
            communicationData.classification,
            communicationData.classifications
          )}
          predefine={communicationData.predefine}
          team={communicationData.team}
          concession={communicationData.concession}
          road={communicationData.road}
          pkInit={`${communicationData.pkInit} km`}
          pkEnd={`${communicationData.pkEnd} km`}
          track={communicationData.track}
          roadDirections={communicationData.roadDirections}
          direction={communicationData.direction}
          city={communicationData.city}
          lane={communicationData.lane}
          margin={communicationData.margin}
          leftButton={t('communications:edit')}
          rightButton={
            actionData?.editCommunication ? t('buttons:save') : undefined
          }
          description={communicationData.description}
          notes={communicationData.notes}
          onEditCommunication={handleEditCommunication}
          onSaveCommunication={handleSaveData}
          storedDataOTSelected={communicationData.storedDataOTSelected}
          changeStateTag={!actionData?.editCommunication && currentRole !== Role.Tablet}
          orderType={OrderType.Communication}
          state={communicationData.state}
          revalidatorFunction={refetchData}
          orderId={communicationData.id}
          communicationId={communicationData.communicationID}
        />
      )}
    </Container>
  );
}
